// Business hours + escalation settings (FR-WORK-003). One document per workspace.
//   days[]      weekly opening hours in the workspace timezone
//   offHours    what visitors get when they ask for a human while closed: queue (agents answer when back) or an offline message
//   escalation  wait-time alert (minutes) for unassigned escalations, and who is notified
import { keyed } from "./core";
import { profilesStore, settingsStore } from "./core";

export const hoursStore = keyed("businessHours", {});

export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
export const TIMEZONES = ["Asia/Kolkata", "UTC", "America/New_York", "Europe/London"];
export const OFF_HOURS_MODES = ["queue", "offline"];
export const NOTIFY_OPTIONS = ["any_agent", "admins"];
export const DEFAULT_OFFLINE_MESSAGE = "Our team is offline right now. Leave your details and we will get back to you when we reopen.";

export const defaultHours = () => ({
  days: DAY_KEYS.map((day) => ({ day, open: !["sat", "sun"].includes(day), from: "09:00", to: "18:00" })),
  offHours: { mode: "queue", offlineMessage: DEFAULT_OFFLINE_MESSAGE },
  escalation: { waitAlertMinutes: 5, notify: "any_agent" }
});

export function timezoneFor(tenantId) {
  const profileTz = (profilesStore.all()[tenantId] || {}).timezone;
  const settingsTz = (settingsStore.all()[tenantId] || {}).timezone;
  const tz = TIMEZONES.includes(profileTz) ? profileTz : settingsTz;
  return TIMEZONES.includes(tz) ? tz : "Asia/Kolkata";
}

export function getHours(tenantId) {
  const saved = hoursStore.all()[tenantId];
  const base = defaultHours();
  if (!saved) return base;
  return { days: saved.days || base.days, offHours: { ...base.offHours, ...(saved.offHours || {}) }, escalation: { ...base.escalation, ...(saved.escalation || {}) } };
}

export function saveHours(tenantId, doc) {
  const all = hoursStore.all();
  all[tenantId] = doc;
  hoursStore.save(all);
}

const toMinutes = (hhmm) => { const [h, m] = String(hhmm).split(":").map(Number); return h * 60 + m; };

/** Local weekday key + minutes since midnight in `tz` for the instant `at`. */
export function localClock(tz, at = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(at);
  const get = (type) => parts.find((p) => p.type === type).value;
  const day = get("weekday").slice(0, 3).toLowerCase();
  return { day, minutes: Number(get("hour")) * 60 + Number(get("minute")), label: `${get("weekday")} ${get("hour")}:${get("minute")}` };
}

export function isOpenAt(doc, tz, at = new Date()) {
  const { day, minutes } = localClock(tz, at);
  const today = doc.days.find((d) => d.day === day);
  return !!(today && today.open && minutes >= toMinutes(today.from) && minutes < toMinutes(today.to));
}

/** What the visitor widget and escalation logic need to know right now. */
export function availabilityFor(tenantId, at = new Date()) {
  const doc = getHours(tenantId);
  const tz = timezoneFor(tenantId);
  const open = isOpenAt(doc, tz, at);
  return { open, timezone: tz, mode: doc.offHours.mode, offlineMessage: doc.offHours.offlineMessage, waitAlertMinutes: doc.escalation.waitAlertMinutes };
}
